import React, { useEffect, useState } from 'react'

import cn from 'classnames'

import ChevronDownIcon from '@src/components/icons/ChevronDown'
import SelectPaymentMethodModal from '@src/pages/FiatExchange/components/SelectPaymentMethodModal'
import { getPaymentMethodDisplayName, getPaymentMethodIcon } from '@src/pages/FiatExchange/utils/paymentMethodUtils'
import { PaymentMethod } from '@src/types/coinbase'

interface PaymentMethodSelectionPillProps {
    /** Current selected payment method (if any) */
    paymentMethod?: PaymentMethod

    /** Callback when user selects a new payment method */
    onChange: (paymentMethod?: PaymentMethod) => void

    /** Available payment methods to choose from */
    availablePaymentMethods: PaymentMethod[]

    /** Whether the payment methods are loading */
    isLoading?: boolean

    /** Whether to show the chevron icon */
    useChevronDown?: boolean

    /** Ramp direction (ONRAMP or OFFRAMP) */
    rampDirection?: 'ONRAMP' | 'OFFRAMP'

    /** Fiat currency symbol for onramp */
    fiatCurrencySymbol?: string

    /** Token symbol for offramp */
    tokenSymbol?: string
}

/**
 * A pill-like UI to display the currently selected payment method
 * and open a modal for selection.
 */
const PaymentMethodSelectionPill: React.FC<PaymentMethodSelectionPillProps> = ({
    paymentMethod,
    onChange,
    availablePaymentMethods,
    isLoading = false,
    useChevronDown = true,
    rampDirection = 'ONRAMP',
    fiatCurrencySymbol,
    tokenSymbol,
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false)

    // Pre-select the first available payment method when methods are loaded
    useEffect(() => {
        if (!isLoading && !paymentMethod && availablePaymentMethods.length > 0) {
            onChange(availablePaymentMethods[0])
        }
    }, [availablePaymentMethods, isLoading, paymentMethod, onChange])

    const handleOpenModal = () => setIsOpen(true)
    const handleCloseModal = () => setIsOpen(false)

    return (
        <div className='relative'>
            <div
                className={cn(
                    'flex cursor-pointer flex-col rounded-md border px-3 py-2',
                    isOpen ? 'border-mach-modal-border' : 'bg-mach-input border-transparent',
                    isLoading && 'cursor-not-allowed opacity-50'
                )}
                onClick={isLoading ? undefined : handleOpenModal}
            >
                <div className='flex w-full items-center justify-between'>
                    <div className='flex items-center gap-2'>
                        <div className='h-5 w-5 flex-shrink-0'>
                            {isLoading
                                ? null
                                : getPaymentMethodIcon(paymentMethod, { className: 'text-mach-color-text-secondary' })}
                        </div>
                        <p className='max-w-[120px] truncate text-sm font-medium'>
                            {isLoading ? 'Loading...' : getPaymentMethodDisplayName(paymentMethod)}
                        </p>
                    </div>
                    {useChevronDown && <ChevronDownIcon className='h-4 w-4' />}
                </div>

                {/* Show limits if available */}
                {!isLoading && paymentMethod?.limits && paymentMethod.limits.length > 0 && (
                    <div className='text-mach-color-text-tertiary mt-1 text-xs'>
                        {(() => {
                            // Determine which symbol to use
                            const symbol =
                                rampDirection === 'ONRAMP'
                                    ? fiatCurrencySymbol || paymentMethod.limits[0].currency
                                    : tokenSymbol || paymentMethod.limits[0].currency

                            return (
                                <>
                                    Min:{' '}
                                    {paymentMethod.limits[0].min ? `${paymentMethod.limits[0].min} ${symbol}` : 'N/A'} |
                                    Max:{' '}
                                    {paymentMethod.limits[0].max ? `${paymentMethod.limits[0].max} ${symbol}` : 'N/A'}
                                </>
                            )
                        })()}
                    </div>
                )}
            </div>

            <SelectPaymentMethodModal
                isOpen={isOpen}
                onClose={handleCloseModal}
                selectedPaymentMethod={paymentMethod}
                setSelectedPaymentMethod={newMethod => {
                    onChange(newMethod)
                }}
                paymentMethods={availablePaymentMethods}
                rampDirection={rampDirection}
                fiatCurrencySymbol={fiatCurrencySymbol}
                tokenSymbol={tokenSymbol}
            />
        </div>
    )
}

export default PaymentMethodSelectionPill
